import { useMemo, useState } from 'react'
import type { QueueInfo } from '@/api/types'
import { IconButton } from '@/components/ui/Button'
import { EmptyState, ErrorState } from '@/components/ui/EmptyState'
import { Icons } from '@/components/ui/Icons'
import { LoadingBlock } from '@/components/ui/Spinner'
import { Check } from './Check'
import styles from './MonitoringPage.module.css'
import { formatCount, queueKey } from './target'

interface QueueListProps {
  queues: QueueInfo[] | undefined
  loading: boolean
  error: unknown
  query: string
  /** Change in message count since the previous fetch, keyed by storage/name. */
  deltas: Map<string, number>
  onClear: (queue: QueueInfo) => void
}

interface Group {
  storage: string
  total: number
  queues: QueueInfo[]
}

function formatDelta(delta: number): string {
  return `(${delta > 0 ? '+' : '−'}${formatCount(Math.abs(delta))})`
}

export function QueueList({ queues, loading, error, query, deltas, onClear }: QueueListProps) {
  const [showEmpty, setShowEmpty] = useState(false)

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const byStorage = new Map<string, Group>()
    for (const queue of queues ?? []) {
      const group = byStorage.get(queue.storage) ?? { storage: queue.storage, total: 0, queues: [] }
      group.total += queue.messageCount
      byStorage.set(queue.storage, group)
      if (!showEmpty && queue.messageCount === 0) continue
      if (needle && !queue.name.toLowerCase().includes(needle)) continue
      group.queues.push(queue)
    }
    return [...byStorage.values()].filter((group) => group.queues.length > 0)
  }, [queues, query, showEmpty])

  if (error) return <ErrorState error={error} />
  if (loading || !queues) return <LoadingBlock />

  const hasMessages = queues.some((queue) => queue.messageCount > 0)
  const emptyText = !hasMessages && !showEmpty ? 'No message in any queue' : query.trim() ? 'No queue matches the filter' : 'No queue'

  return (
    <>
      <Check checked={showEmpty} onChange={setShowEmpty}>
        Show empty queues
      </Check>
      {groups.length === 0 ? (
        <EmptyState>{emptyText}</EmptyState>
      ) : (
        <ul className={styles.groupList}>
          {groups.map((group) => (
            <li key={group.storage} className={styles.group}>
              <div className={styles.groupHeader}>
                <span className={styles.groupName}>{group.storage}</span>
                <span className={styles.count} title="Messages in this storage">
                  {formatCount(group.total)}
                </span>
              </div>
              <ul className={styles.queueList}>
                {group.queues.map((queue) => {
                  const delta = deltas.get(queueKey(queue))
                  return (
                    <li key={queue.name} className={styles.queueRow}>
                      <span className={styles.queueName}>{queue.name}</span>
                      <span className={styles.count}>
                        <strong>{formatCount(queue.messageCount)}</strong>
                        {delta !== undefined && (
                          <span className={styles.delta} title="Change since the previous refresh">
                            {formatDelta(delta)}
                          </span>
                        )}
                      </span>
                      <IconButton label={`Clear messages in ${queue.name}`} onClick={() => onClear(queue)}>
                        <Icons.Trash size={14} />
                      </IconButton>
                    </li>
                  )
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}
